/**
 * Post-Auth0 session flow: organisations → `my-access` filter → pick org
 * (lastActiveOrg hint) → `refresh-token` (atomic swap, mirrors
 * `Navbar.vue:2494-2570`) → `/user/permission`. Ported from
 * `Auth0Callback.vue` + `Navbar.vue` per the design spec Part C.
 */
import { API_BASE_URL } from "@/tenant/resolve";
import type {
  GetUserOrganisationsResponse,
  NavBarItem,
  RefreshTokenRequest,
  RefreshTokenResponse,
  TenantAppMyAccessResponse,
  UserOrganisation,
  UserPermissionResponse,
} from "@/tenant/types";

/**
 * Server-side truth: a 403 from `POST /user-access/refresh-token` means the
 * caller's organisation is not part of `tenantAppSlug` (revoked, or never
 * granted) — the shell treats this identically to the `my-access` empty-list
 * case (design addendum, binding). Distinguished from a generic `Error` so
 * the store can route to NoAccess instead of showing a bare failure message.
 */
export class TenantAccessDeniedError extends Error {
  constructor(message = "Not part of this app") {
    super(message);
    this.name = "TenantAccessDeniedError";
  }
}

interface ApiRequestOptions {
  method?: "GET" | "POST";
  body?: unknown;
  bearer?: string | null;
  orgToken?: string | null;
  /** Throw `TenantAccessDeniedError` (instead of a generic `Error`) on a 403 response. */
  accessDeniedOn403?: boolean;
}

async function apiRequest<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    "Time-Zone": Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (opts.bearer) headers.Authorization = opts.bearer;
  if (opts.orgToken) headers.apikey = opts.orgToken;

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: opts.method || "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (res.status === 403 && opts.accessDeniedOn403) {
    throw new TenantAccessDeniedError();
  }
  if (!res.ok) {
    const detail = await res.json().catch(() => null);
    throw new Error(`${opts.method || "GET"} ${path} failed: ${res.status}${detail?.message ? " " + detail.message : ""}`);
  }
  return res.json();
}

/** `GET /user-access/organisation` — every org the caller belongs to (unfiltered by tenant). */
export async function fetchOrganisations(bearer: string): Promise<UserOrganisation[]> {
  const body = await apiRequest<GetUserOrganisationsResponse>("/user-access/organisation", { bearer });
  return body.result || [];
}

/** `GET /tenant-apps/my-access?slug=` — Appendix A3 (keyed by the resolved app's slug, not its hostname). */
export async function fetchMyAccess(slug: string, bearer: string): Promise<TenantAppMyAccessResponse> {
  return apiRequest<TenantAppMyAccessResponse>(`/tenant-apps/my-access?slug=${encodeURIComponent(slug)}`, { bearer });
}

/**
 * `POST /user-access/refresh-token` — mints the org-scoped MetaShip token.
 * `tenantAppSlug` is required on EVERY call from this shell (initial sign-in,
 * org switch, proactive refresh) — the backend enforces the access gate on
 * this endpoint, not just on `my-access`; a 403 here throws
 * `TenantAccessDeniedError`.
 */
export async function mintOrgToken(
  org: UserOrganisation,
  bearer: string,
  tenantAppSlug: string,
): Promise<RefreshTokenResponse> {
  const requestBody: RefreshTokenRequest = { organisation: org.token, organisationName: org.name, tenantAppSlug };
  const body = await apiRequest<{ data: RefreshTokenResponse }>("/user-access/refresh-token", {
    method: "POST",
    bearer,
    accessDeniedOn403: true,
    body: requestBody,
  });
  return body.data;
}

/** `GET /user/permission` — the NavBarList filtered to this user+org's access. */
export async function fetchPermissions(bearer: string, orgToken: string): Promise<NavBarItem[]> {
  const body = await apiRequest<UserPermissionResponse>("/user/permission", { bearer, orgToken });
  return body.result || [];
}

/* ── Pure logic (unit-testable without a network) ────────────────────────── */

function lastActiveOrgKey(userId: number | string): string {
  return `metaship:lastActiveOrg:${userId}`;
}

export function readLastActiveOrgId(userId: number | string): string | null {
  try {
    return localStorage.getItem(lastActiveOrgKey(userId));
  } catch {
    return null;
  }
}

export function rememberLastActiveOrgId(userId: number | string, orgId: number): void {
  try {
    localStorage.setItem(lastActiveOrgKey(userId), String(orgId));
  } catch {
    // best-effort — a missed hint just falls back to "first usable org" next login
  }
}

function isUsable(org: UserOrganisation): boolean {
  return org.accountStatus !== "SUSPENDED" && org.accountStatus !== "BANNED";
}

/**
 * In-app org switcher default (Sidebar footer): prefer the remembered last-active org
 * when it's still usable, else the first usable org. Mirrors
 * `Navbar.vue:3120` `pickDefaultOrganisation` — always resolves to SOME org
 * when one exists, since the switcher only ever offers orgs the user may use.
 */
export function pickDefaultOrganisation(
  orgs: UserOrganisation[],
  lastActiveOrgId: string | number | null,
): UserOrganisation | null {
  if (lastActiveOrgId != null) {
    const hit = orgs.find((o) => String(o.id) === String(lastActiveOrgId) && isUsable(o));
    if (hit) return hit;
  }
  return orgs.find(isUsable) || null;
}

/** WHITE_LABEL → `[organisationId]` (0 or 1 row); PRODUCT → every accessible org, unfiltered order. */
export function resolveAccessibleOrganisations(
  allOrgs: UserOrganisation[],
  myAccess: TenantAppMyAccessResponse,
): UserOrganisation[] {
  if (!myAccess.found || myAccess.organisationIds.length === 0) return [];
  const allowed = new Set(myAccess.organisationIds);
  return allOrgs.filter((o) => allowed.has(o.id));
}

export type AccessGateDecision =
  | { mode: "none" }
  | { mode: "auto"; org: UserOrganisation }
  | { mode: "pick"; orgs: UserOrganisation[] };

/**
 * The sign-in gate decision: no accessible org → NoAccess; exactly one, or a
 * remembered last-active org among several → sign straight in; otherwise show
 * the org picker. Deliberately does NOT fall back to "first org" on ties (that
 * would silently drop a multi-org user into a random org) — that fallback is
 * `pickDefaultOrganisation`'s job, for the already-signed-in switcher only.
 */
export function decideAccessGate(
  accessibleOrgs: UserOrganisation[],
  lastActiveOrgId: string | number | null,
): AccessGateDecision {
  if (accessibleOrgs.length === 0) return { mode: "none" };
  if (accessibleOrgs.length === 1) return { mode: "auto", org: accessibleOrgs[0]! };
  if (lastActiveOrgId != null) {
    const hit = accessibleOrgs.find((o) => String(o.id) === String(lastActiveOrgId));
    if (hit) return { mode: "auto", org: hit };
  }
  return { mode: "pick", orgs: accessibleOrgs };
}
